// ไฟล์: frontend/src/pages/LoginPage.js
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import './AuthPages.css';

const LoginPage = ({ onLogin }) => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // จัดการการเปลี่ยนแปลงข้อมูลในฟอร์ม
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // ล้างข้อความผิดพลาดเมื่อผู้ใช้แก้ไขข้อมูล
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  // ตรวจสอบข้อมูลในฟอร์ม
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.username.trim()) {
      newErrors.username = 'กรุณาระบุชื่อผู้ใช้';
    }
    
    if (!formData.password) {
      newErrors.password = 'กรุณาระบุรหัสผ่าน';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // จัดการการส่งฟอร์ม
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post('/auth/login', formData);
      
      // บันทึกข้อมูลผู้ใช้และ token
      onLogin(response.data.user, response.data.token);
      
      // แสดงข้อความแจ้งเตือนการเข้าสู่ระบบสำเร็จ
      Swal.fire({
        icon: 'success',
        title: 'เข้าสู่ระบบสำเร็จ',
        text: `ยินดีต้อนรับ ${response.data.user.fullname}`,
        timer: 2000,
        showConfirmButton: false
      });
      
      // นำทางไปยังหน้าหลัก
      navigate('/');
    } catch (error) {
      console.error('Login error:', error);
      
      // แสดงข้อความแจ้งเตือนข้อผิดพลาด
      Swal.fire({
        icon: 'error',
        title: 'เข้าสู่ระบบไม่สำเร็จ',
        text: error.response?.data?.message || 'เกิดข้อผิดพลาดในการเข้าสู่ระบบ กรุณาลองใหม่อีกครั้ง',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h2>เข้าสู่ระบบ</h2>
          <p>เข้าสู่ระบบเพื่อแบ่งปันและเรียนรู้ร่วมกัน</p>
        </div>
        
        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="username" className="form-label">
              <i className="fas fa-user"></i> ชื่อผู้ใช้
            </label>
            <input
              type="text"
              id="username"
              name="username"
              className={`form-control ${errors.username ? 'is-invalid' : ''}`}
              placeholder="ชื่อผู้ใช้ของคุณ"
              value={formData.username}
              onChange={handleChange}
            />
            {errors.username && <div className="invalid-feedback">{errors.username}</div>}
          </div>
          
          <div className="form-group">
            <label htmlFor="password" className="form-label">
              <i className="fas fa-lock"></i> รหัสผ่าน
            </label>
            <input
              type="password"
              id="password"
              name="password"
              className={`form-control ${errors.password ? 'is-invalid' : ''}`}
              placeholder="รหัสผ่านของคุณ"
              value={formData.password}
              onChange={handleChange}
            />
            {errors.password && <div className="invalid-feedback">{errors.password}</div>}
          </div>
          
          <div className="form-group">
            <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> กำลังเข้าสู่ระบบ...
                </>
              ) : (
                <>
                  <i className="fas fa-sign-in-alt"></i> เข้าสู่ระบบ
                </>
              )}
            </button>
          </div>
        </form>
        
        <div className="auth-footer">
          <p>ยังไม่มีบัญชีใช่หรือไม่? <Link to="/register">สมัครสมาชิก</Link></p>
        </div>
      </div>
      
      <div className="auth-info">
        <div className="auth-logo">
          <img src={process.env.REACT_APP_LOGO_URL || 'https://www.i-pic.info/i/krVq1004512.jpg'} alt="TeacherShare" />
          <h1>TeacherShare</h1>
        </div>
        <h2>แบ่งปัน เรียนรู้ พัฒนา ไปด้วยกัน</h2>
        <p>
          TeacherShare คือศูนย์กลางสำหรับครูในการแบ่งปันความรู้ สื่อการสอน และนวัตกรรมทางการศึกษา
          เพื่อพัฒนาการเรียนการสอนและสร้างสังคมแห่งการเรียนรู้ร่วมกัน
        </p>
        <div className="auth-features">
          <div className="auth-feature">
            <i className="fas fa-book"></i>
            <span>คลังสื่อการสอนคุณภาพ</span>
          </div>
          <div className="auth-feature">
            <i className="fas fa-lightbulb"></i>
            <span>นวัตกรรมและแนวปฏิบัติที่ดี</span>
          </div>
          <div className="auth-feature">
            <i className="fas fa-users"></i>
            <span>ชุมชนครูทั่วประเทศ</span>
          </div>
          <div className="auth-feature">
            <i className="fas fa-trophy"></i>
            <span>รางวัลและการยกย่อง</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
